import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaWhatsapp } from 'react-icons/fa';
import emailjs from '@emailjs/browser';

const Contact = () => {
  const formRef = useRef();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    websiteType: '',
    message: '',
  });
  
  const [formStatus, setFormStatus] = useState({
    submitted: false,
    submitting: false,
    error: false,
    message: ''
  });
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus({ 
      submitted: false, 
      submitting: true,
      error: false,
      message: ''
    });
    
    // EmailJS service configuration
    // Replace with your actual EmailJS service ID, template ID and public key
    emailjs.sendForm(
      'service_xxxxxxx', // Service ID
      'template_xxxxxxx', // Template ID
      formRef.current,
      'YOUR_PUBLIC_KEY' // Public Key
    )
      .then((result) => {
        setFormStatus({ 
          submitted: true, 
          submitting: false,
          error: false,
          message: 'Thank you for your message! We\'ll get back to you shortly.'
        });
        // Reset form after submission
        setFormData({
          name: '',
          email: '',
          phone: '',
          websiteType: '',
          message: '',
        });
        console.log(result.text);
      })
      .catch((error) => {
        setFormStatus({ 
          submitted: false, 
          submitting: false, 
          error: true,
          message: 'There was an error sending your message. Please try again.'
        });
        console.error(error);
      });
  };
  
  const websiteTypes = [
    { value: '', label: 'Select Website Type' },
    { value: 'portfolio', label: 'Portfolio Website' },
    { value: 'business', label: 'Business Website' },
    { value: 'ecommerce', label: 'E-Commerce Store' },
    { value: 'personal', label: 'Personal Brand Site' },
    { value: 'booking', label: 'Booking/Appointment Site' },
    { value: 'educational', label: 'Educational Platform' },
    { value: 'landing', label: 'Landing Page' },
    { value: 'event', label: 'Event/Wedding Site' },
    { value: 'ngo', label: 'NGO/Donation Platform' },
    { value: 'other', label: 'Other (Please specify in message)' }
  ];

  return (
    <div className="min-h-screen pt-28 pb-20 bg-cyber-dark">
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl font-orbitron font-bold mb-6">
            Get In <span className="text-cyber-pink">Touch</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Ready to bring your digital vision to life? Contact us today to discuss your project
            and get a free consultation.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="cyber-card p-8"
          >
            <h2 className="text-2xl font-orbitron font-bold mb-6 text-cyber-neon">Send Us a Message</h2>
            
            {formStatus.submitted ? (
              <div className="bg-cyber-neon/20 border border-cyber-neon text-white p-4 rounded">
                <p>{formStatus.message}</p>
              </div>
            ) : (
              <form ref={formRef} onSubmit={handleSubmit}>
                <div className="mb-6">
                  <label htmlFor="name" className="block text-white mb-2">Your Name <span className="text-cyber-pink">*</span></label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="email" className="block text-white mb-2">Email Address <span className="text-cyber-pink">*</span></label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-white mb-2">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                    />
                  </div>
                </div>
                
                <div className="mb-6">
                  <label htmlFor="websiteType" className="block text-white mb-2">Website Type <span className="text-cyber-pink">*</span></label>
                  <select
                    id="websiteType"
                    name="websiteType"
                    value={formData.websiteType}
                    onChange={handleChange}
                    required
                    className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                  >
                    {websiteTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                
                <div className="mb-8">
                  <label htmlFor="message" className="block text-white mb-2">Your Message <span className="text-cyber-pink">*</span></label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                  ></textarea>
                </div>
                
                {formStatus.error && (
                  <div className="bg-cyber-pink/20 border border-cyber-pink text-white p-4 rounded mb-6">
                    <p>{formStatus.message}</p>
                  </div>
                )}
                
                <button 
                  type="submit" 
                  className="cyber-button w-full md:w-auto"
                  disabled={formStatus.submitting}
                >
                  <span>{formStatus.submitting ? 'Sending...' : 'Send Message'}</span>
                </button>
              </form>
            )}
          </motion.div>
          
          {/* Map and Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-8"
          >
            {/* Cyberpunk Styled Map */}
            <div className="cyber-card p-1 h-80 overflow-hidden">
              <div className="relative w-full h-full">
                {/* Replace with an actual map integration if needed */}
                <div className="absolute inset-0 bg-cyber-gray">
                  <div className="w-full h-full bg-[linear-gradient(90deg,rgba(157,78,221,0.1)_1px,transparent_1px),linear-gradient(0deg,rgba(157,78,221,0.1)_1px,transparent_1px)] bg-[size:20px_20px]"></div>
                  
                  {/* City-like grid */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-full max-w-xs">
                      {/* Horizontal lines */}
                      {[...Array(10)].map((_, i) => (
                        <div 
                          key={i} 
                          className="absolute h-[1px] bg-cyber-pink opacity-60 left-0 right-0"
                          style={{ top: `${10 + (i * 8)}%` }}
                        ></div>
                      ))}
                      
                      {/* Vertical lines */}
                      {[...Array(10)].map((_, i) => (
                        <div 
                          key={i} 
                          className="absolute w-[1px] bg-cyber-neon opacity-60 top-0 bottom-0"
                          style={{ left: `${10 + (i * 8)}%` }}
                        ></div>
                      ))}
                      
                      {/* Location pin */}
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                        <div className="w-16 h-16 rounded-full bg-cyber-pink/20 animate-ping"></div>
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                          <div className="w-8 h-8 rounded-full bg-cyber-pink flex items-center justify-center">
                            <FaMapMarkerAlt className="text-white" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Overlay text */}
                  <div className="absolute bottom-4 right-4 text-right">
                    <p className="text-cyber-neon font-orbitron text-xs opacity-70">COORDINATES: 37.7749° N, 122.4194° W</p>
                    <p className="text-white font-orbitron text-sm">CYBERNEST HEADQUARTERS</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Contact Info */}
            <div className="cyber-card p-8 space-y-6">
              <h2 className="text-2xl font-orbitron font-bold mb-4 text-cyber-pink">Contact Information</h2>
              
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-full bg-cyber-gray flex items-center justify-center mr-4">
                  <FaMapMarkerAlt className="text-cyber-pink text-xl" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Visit Our Office</p>
                  <p className="text-white">digital city, delhi</p>
                </div>
              </div>
              
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-full bg-cyber-gray flex items-center justify-center mr-4">
                  <FaEnvelope className="text-cyber-neon text-xl" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Email Us</p>
                  <a href="mailto:ankitmaharshi55@gmail.com" className="text-white hover:text-cyber-neon transition-colors">ankitmaharshi55@gmail.com</a>
                </div>
              </div>
              
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-full bg-cyber-gray flex items-center justify-center mr-4">
                  <FaPhone className="text-cyber-purple text-xl" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Call Us</p>
                  <a href="tel:+919468532536" className="text-white hover:text-cyber-purple transition-colors">+91-9468532536</a>
                </div>
              </div>
              
              {/* Quick Action Buttons */}
              <div className="pt-6 grid grid-cols-2 gap-4">
                <a href="https://wa.me/919468532536" className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors">
                  <FaWhatsapp className="text-xl" />
                  <span>WhatsApp Us</span>
                </a>
                <a href="mailto:ankitmaharshi55@gmail.com" className="flex items-center justify-center gap-2 px-4 py-3 bg-cyber-blue hover:bg-cyber-blue/80 text-white rounded-lg transition-colors">
                  <FaEnvelope className="text-xl" />
                  <span>Email Us</span>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* Work Hours & FAQ Link */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="cyber-card p-8 text-center"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-orbitron font-bold mb-4 text-white">Working Hours</h3>
              <ul className="space-y-2 text-gray-300">
                <li className="flex justify-between">
                  <span>Monday - Friday:</span>
                  <span>9:00 AM - 6:00 PM</span>
                </li>
                <li className="flex justify-between">
                  <span>Saturday:</span>
                  <span>10:00 AM - 4:00 PM</span>
                </li>
                <li className="flex justify-between">
                  <span>Sunday:</span>
                  <span>Closed</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-orbitron font-bold mb-4 text-white">Have Questions?</h3>
              <p className="text-gray-300 mb-4">Check out our frequently asked questions or contact our customer support team.</p>
              <a href="/pricing#faq" className="cyber-button inline-block">
                <span>View FAQ</span>
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact; 